import { CardCvcElement, CardExpiryElement, CardNumberElement } from "@stripe/react-stripe-js";
import { goToPlacedOrder } from "./goToPlacedOrder";
import React, { useState } from "react";
import { useTheme } from "../../../../hooks/ThemeContext";
import ClipLoader from "react-spinners/ClipLoader";
import StripeService from "../../../../service/StripeService";
import { getNames, getCode } from 'country-list';
import TimeService from "../../../../service/TimeService";
import ToastService from "../../../../service/ToastService";
import { useRestaurant } from "../../../../hooks/RestaurantContext";
import { getRemoteEnv } from '../../../../config/remoteEnv';

// Add global style to hide webkit scrollbar
const style = document.createElement('style');
style.textContent = `
  .hide-scrollbar::-webkit-scrollbar {
    display: none;
  }
`;
document.head.appendChild(style);

type Props = {
    stripe: any,
    elements: any,
    order: any,
    setCart: any,
    agreeToMarketing: boolean,
    marketingRefused?: boolean,
    amount: number,
    tipAmount: number,
    tableNumber: number | null;
    formData: any,
    onBack: any,
    onSuccessReturn: any,
    updateQuantity: any,
    removeOption: any,
    productData: any[],
    menuSettings: any,
    openingHours: any,
    orderType: string,
    deliveryAddress: any,
    deliveryQuote: any,
    testMode: boolean
}

const CardDetails = (props: Props) => {
    const { theme } = useTheme();
    const { restaurantId } = useRestaurant();
    const [loading, setLoading] = useState(false);
    const [formErrors, setFormErrors] = useState<string[]>([]);
    const [cardComplete, setCardComplete] = useState({
        numberComplete: false,
        expiryComplete: false,
        cvcComplete: false
    });

    const [cardholderName, setCardholderName] = useState("");
    const [address, setAddress] = useState({
        line1: "",
        line2: "",
        city: "",
        state: "",
        country: getRemoteEnv().countryCode,
        postal_code: "",
    });

    const styles = {
        container: {
            display: "flex",
            flexDirection: "column" as const,
            height: "100%",
            color: theme.cartTextColor,
            position: "relative" as const,
        },
        content: {
            flex: 1,
            overflowY: "auto" as const,
            paddingBottom: "80px", // Space for footer
            msOverflowStyle: "none" as const, // IE and Edge
            scrollbarWidth: "none" as const, // Firefox
            WebkitOverflowScrolling: "touch" as const,
            overflow: "-moz-scrollbars-none" as const,
        },
        mainContent: {
            padding: "0 0px",
        },
        backButton: {
          width: "auto",
          padding: "8px 16px",
          cursor: "pointer",
          backgroundColor: theme.cartButtonColor,
          color: theme.cartButtonTextColor,
          border: "none",
          borderRadius: "10px",
          fontSize: "14px",
          fontWeight: "500",
          alignSelf: "flex-start",
          transition: "transform 0.2s ease",
        },
        footer: {
            position: "absolute" as const,
            bottom: 0,
            left: 0,
            right: 0,
            padding: "16px 20px",
            backgroundColor: theme.cartBackgroundColor || "#ffffff",
            borderTop: "1px solid rgba(0,0,0,0.1)",
        },
        formGroup: {
            display: "flex",
            flexDirection: "column" as const,
            gap: "8px",
            marginBottom: "12px",
        },
        sectionTitle: {
            fontSize: "19px",
            fontWeight: 800,
            letterSpacing: "-0.3px",
            margin: "0",
            marginTop: "22px",
            marginBottom: "14px",
        },
        label: {
            fontSize: "12px",
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase" as const,
            opacity: 0.6,
        },
        select: {
            height: "46px",
            width: "100%",
            padding: "0 14px",
            border: `1.5px solid ${theme.cartSummaryBorder || "grey"}`,
            borderRadius: "14px",
            background: theme.searchBarColor || "none",
            color: theme.cartTextColor,
            fontSize: "14px",
            fontFamily: "inherit",
            boxSizing: "border-box" as const,
        },
        input: {
            height: "46px",
            width: "100%",
            padding: "0 14px",
            border: `1.5px solid ${theme.cartSummaryBorder || "grey"}`,
            borderRadius: "14px",
            background: theme.searchBarColor || "none",
            color: theme.cartTextColor,
            fontSize: "16px",
            fontFamily: "inherit",
            boxSizing: "border-box" as const,
        },
        stripeElement: {
            padding: "13px 14px",
            border: `1.5px solid ${theme.cartSummaryBorder || "grey"}`,
            borderRadius: "14px",
            background: theme.searchBarColor || "none",
            boxSizing: "border-box" as const,
        },
        cardRow: {
            display: "flex",
            gap: "16px",
            marginTop: "8px",
            marginBottom: "16px",
        },
        cardColumn: {
            flex: 1,
            display: "flex",
            flexDirection: "column" as const,
            gap: "8px",
        },
        payButton: {
            height: "52px",
            width: "100%",
            cursor: "pointer",
            backgroundColor: theme.payButtonColor,
            color: theme.payButtonTextColor,
            border: "none",
            borderRadius: "14px",
            fontSize: "15px",
            fontWeight: 700,
            fontFamily: "inherit",
        },
        loader: {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "48px",
        },
        errorList: {
            color: "#e5484d",
            backgroundColor: "rgba(229, 72, 77, 0.08)",
            border: "1px solid rgba(229, 72, 77, 0.25)",
            padding: "12px 14px",
            borderRadius: "14px",
            marginTop: "8px",
        },
        errorItem: {
            fontSize: "14px",
            marginBottom: "4px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
        },
    };

    const scrollToBottom = () => {
        setTimeout(() => {
          const cityInput = document.querySelector('input[placeholder="City"]');
          if (cityInput) {
            // Scroll the city input to the top of the view instead of center
            cityInput.scrollIntoView({ block: 'start', behavior: 'auto' });
            
            // Make sure parent scrollable containers are also scrolled properly
            let parent = cityInput.parentElement;
            while (parent) {
              const style = window.getComputedStyle(parent);
              if (style.overflowY === 'scroll' || style.overflowY === 'auto') {
                // Ensure the scrolling is applied
                const inputRect = cityInput.getBoundingClientRect();
                const parentRect = parent.getBoundingClientRect();
                
                // If input top is below parent top, adjust scroll
                if (inputRect.top > parentRect.top) {
                  parent.scrollTop += (inputRect.top - parentRect.top);
                }
              }
              parent = parent.parentElement;
            }
          }
        }, 50);
      };

    const handleCardChange = (event: any, field: string) => {
        setCardComplete((prev) => ({
            ...prev,
            [`${field}Complete`]: event.complete,
        }));
    };

    const handleAddressChange = (event: any, field: string) => {
        if (field === "country") {
            setAddress((prev) => ({
                ...prev,
                [field]: event.target.value,
            }));
        } else {
            setAddress((prev) => ({
                ...prev,
                [field]: event.target.value,
            }));
        }
    };

    const validateForm = () => {
        let errors: string[] = [];
        let valid = true;

        if (!cardholderName) {
          errors.push("Please enter the cardholder's name.");
          valid = false;
      }


        if (!cardComplete.numberComplete || !cardComplete.expiryComplete || !cardComplete.cvcComplete) {
            errors.push("Please complete your card details.");
            valid = false;
        }

        if (!address.country || !address.city || !address.line1 || !address.postal_code) {
            errors.push("Please complete your billing address.");
            valid = false;
        }

        if (address.country === "US" && !address.state) {
            errors.push("Please complete your billing address.");
            valid = false;
        }

        setFormErrors(errors);
        
        // If there are errors, scroll to the bottom
        if (!valid) {
            const contentDiv = document.querySelector('.hide-scrollbar');
            if (contentDiv) {
                contentDiv.scrollTo({
                    top: contentDiv.scrollHeight,
                    behavior: 'smooth'
                });
            }
        }
        
        return valid;
    };

    const onPaymentSubmit = async () => {
        let storeOpen = await TimeService.checkStoreIsOpen(props.openingHours);

        if (!storeOpen) {
            props.onSuccessReturn();
            ToastService.showToast("Store is currently closed!", {
                style: {
                    backgroundColor: "darkred",
                    color: "white",
                    minHeight: "80px",
                    borderRadius: "15px",
                },
            });
            return;
        }

        let allowedMenus = await TimeService.getAllowedMenus(props.menuSettings);
        let updatedCart = false;

        for (let orderItem of props.order) {
            let itemObj = props.productData.filter((x) => x.productId === orderItem.id)[0];
            let menuFound = false;
            for (let menuItem of orderItem.menus) {
                if (allowedMenus.includes(menuItem)) {
                    let categoryObj = props.menuSettings.filter((x:any) => x.menuName === menuItem)[0].categories.filter((c: any) => c.id === itemObj.category)[0];
                    if (categoryObj.enabled !== false && !categoryObj.deleted){
                        menuFound = true;
                    }
                }
            }
            if (menuFound == false || itemObj.isDeleted || itemObj.isDisabled || itemObj.outOfStock) {
                updatedCart = true;
                props.updateQuantity(orderItem, 0);
            }

            if (orderItem.additionalOptionIds && orderItem.additionalOptionIds.length > 0) {
                for (let addOption of orderItem.additionalOptionIds) {
                    let additionalOptionObj = props.productData.filter((x) => x.productId === addOption)[0];
                    if (!additionalOptionObj.outOfStock && !additionalOptionObj.isDisabled && !additionalOptionObj.isDeleted) {

                        let addOptionsCategoryObj = props.menuSettings.filter((x: any) => x.menuName === additionalOptionObj.menus[0])[0].categories.filter((c: any) => c.id === additionalOptionObj.category)[0];
                        if (addOptionsCategoryObj.enabled !== false && !addOptionsCategoryObj.deleted){
                            ;
                        }
                        else {
                            props.removeOption(orderItem, addOption);
                            updatedCart = true;
                        }

                    }
                    else {
                        props.removeOption(orderItem, addOption)
                        updatedCart = true;
                    }
                }
            }
            if (updatedCart){
                props.onSuccessReturn();
                ToastService.showToast("Unavailable items have been removed from your cart. Please verify your cart before ordering!", {
                    style: {
                        backgroundColor: "darkred",
                        color: "white",
                        minHeight: "80px",
                        borderRadius: "15px",
                    },
                });
                return;
            }

        }

        if (!validateForm()) {
            return;
        }

        setLoading(true);
        console.log("Props being passed to StripeService:", {
            restaurantId: restaurantId,
            testMode: props.testMode,
            deliveryQuote: props.deliveryQuote,
            orderType: props.orderType,
            deliveryAddress: props.deliveryAddress
        });
        const result = await StripeService.createPaymentIntent(
            props.stripe,
            props.elements,
            props.order,
            props.amount,
            props.tipAmount,
            props.tableNumber,
            props.formData,
            cardholderName,
            address,
            props.orderType,
            props.deliveryAddress,
            props.deliveryQuote,
            props.testMode,
            restaurantId,
            props.agreeToMarketing,
            props.marketingRefused === true
        );
        setLoading(false);

        if (result.status === "SUCCESS") {
            localStorage.setItem(`order_${result.orderCode}`, JSON.stringify(props.order));
            props.setCart([]);
            props.onSuccessReturn();
            ToastService.showToast("Payment Successful!", {
                style: {
                    backgroundColor: "darkgreen",
                    color: "white",
                },
            });
            goToPlacedOrder(result.orderCode, props.orderType, props.deliveryQuote, restaurantId, { orderId: (result as any).orderId ?? null, total: Number(props.amount) || null, itemCount: Array.isArray(props.order) ? props.order.reduce((n: number, it: any) => n + (Number(it?.quantity) || 1), 0) : null });
        } else if (result.status === "QUOTE_EXPIRED") {
            // Go back to cart to regenerate quote
            props.onSuccessReturn(); // This should close the payment modal and return to cart
            ToastService.showToast("Delivery pricing has been updated. Please try again.", {
                style: {
                    backgroundColor: "red",
                    color: "white",
                    minHeight: "80px",
                    borderRadius: "15px",
                },
                autoclose: 5000
            });
        }  else {
            // PPB rejections carry a specific reason (loyalty points, reward
            // validity, minimum spend…) — show it so the customer can act.
            ToastService.showToast((result as any)?.message || "Payment Failed!", {
                style: {
                    backgroundColor: "darkred",
                    color: "white",
                    minHeight: "80px",
                    borderRadius: "15px",
                },
                autoclose: 7000,
            });
        }
    };

    const countries = getNames().map((country) => ({
        label: country,
        value: getCode(country),
    }));

    return (
        <div style={styles.container}>
            <div style={styles.content} className="hide-scrollbar">
                <button style={styles.backButton} onClick={props.onBack}>
                    Back
                </button>

                <div style={styles.mainContent}>
                    <h2 style={styles.sectionTitle}>Billing Details</h2>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Cardholder Name</label>
                        <input
                            value={cardholderName}
                            onChange={(e) => setCardholderName(e.target.value)}
                            style={styles.input}
                            placeholder="Full Name"
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Country</label>
                        <select
                            value={address.country}
                            onChange={(e) => handleAddressChange(e, "country")}
                            style={styles.select}
                        >
                            <option value="">Select a country</option>
                            {countries.filter(x => !x.label.includes("rae")).map((country) => (
                                <option key={country.value} value={country.value}>
                                    {country.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {address.country !== "" && (
                        <>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>Address Line 1</label>
                                <input
                                    value={address.line1}
                                    onChange={(e) => handleAddressChange(e, "line1")}
                                    style={styles.input}
                                    placeholder="Street address"
                                />
                            </div>

                            <div style={styles.formGroup}>
                                <label style={styles.label}>Address Line 2</label>
                                <input
                                    value={address.line2}
                                    onChange={(e) => handleAddressChange(e, "line2")}
                                    style={styles.input}
                                    placeholder="Apartment, suite, unit, etc. (optional)"
                                />
                            </div>

                            <div style={styles.formGroup}>
                                <label style={styles.label}>City</label>
                                <input
                                    value={address.city}
                                    onFocus={scrollToBottom}
                                    onChange={(e) => handleAddressChange(e, "city")}
                                    style={styles.input}
                                    placeholder="City"
                                />
                            </div>

                            {address.country === "US" && (
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>State</label>
                                    <input
                                        value={address.state}
                                        onChange={(e) => handleAddressChange(e, "state")}
                                        style={styles.input}
                                        placeholder="State"
                                    />
                                </div>
                            )}

                            <div style={styles.formGroup}>
                                <label style={styles.label}>Postal Code</label>
                                <input
                                    value={address.postal_code}
                                    onChange={(e) => handleAddressChange(e, "postal_code")}
                                    style={styles.input}
                                    placeholder="Postal code"
                                />
                            </div>
                        </>
                    )}

                    <h2 style={styles.sectionTitle}>Card Details</h2>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Card Number</label>
                        <div style={styles.stripeElement}>
                            <CardNumberElement
                                onChange={(e) => handleCardChange(e, "number")}
                                onFocus={scrollToBottom}
                                options={{
                                    style: {
                                        base: {
                                            fontSize: "16px",
                                            color: theme.cartTextColor,
                                            "::placeholder": {
                                                color: "#aab7c4",
                                            },
                                        },
                                    },
                                }}
                            />
                        </div>
                    </div>

                    <div style={styles.cardRow}>
                        <div style={styles.cardColumn}>
                            <label style={styles.label}>Expiry Date</label>
                            <div style={styles.stripeElement}>
                                <CardExpiryElement
                                    onChange={(e) => handleCardChange(e, "expiry")}
                                    options={{
                                        style: {
                                            base: {
                                                fontSize: "16px",
                                                color: theme.cartTextColor,
                                                "::placeholder": {
                                                    color: "#aab7c4",
                                                },
                                            },
                                        },
                                    }}
                                />
                            </div>
                        </div>

                        <div style={styles.cardColumn}>
                        <label style={styles.label}>CVC</label>
                            <div style={styles.stripeElement}>
                                <CardCvcElement
                                    onChange={(e) => handleCardChange(e, "cvc")}
                                    options={{
                                        style: {
                                            base: {
                                                fontSize: "16px",
                                                color: theme.cartTextColor,
                                                "::placeholder": {
                                                    color: "#aab7c4",
                                                },
                                            },
                                        },
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                    {formErrors.length > 0 && (
                        <div style={styles.errorList}>
                            {formErrors.map((error, index) => (
                                <div key={index} style={styles.errorItem}>
                                    • {error}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div style={styles.footer}>
                {loading ? (
                    <div style={styles.loader}>
                        <ClipLoader color={theme.cartButtonColor} loading={true} size={20} />
                    </div>
                ) : (
                    <button style={styles.payButton} onClick={onPaymentSubmit}>
                        Pay
                    </button>
                )}
            </div>
        </div>
    );
};

export default CardDetails;